<template>
  <div id="sd-root" :class="[
    (sidebarMini || !SDRouteCtx) && 't-sidebar-mini',
    !SDRouteCtx && 't-noapp', topbarFix && 'Topbar--fixed',
    'l-root'
  ]">
    <Topbar v-if="showPanels" :hasSelected="isSDRouteCtxPath && SDRouteCtx" />

    <Sidebar v-if="showPanels" :hasSelected="isSDRouteCtxPath && SDRouteCtx" @sidebar-toggle="onSidebarToggle" />

    <template v-if="showAny">
    <Login v-if="showLogin" key="login" class="anim-appear" />

    <LMain v-else key="main" class="anim-appear" :key="SDRouteCtxUpdateFix">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in" appear>
          <AppNotFound v-if="isSDRouteCtxPath && !SDRouteCtx" />
          <component v-else :is="Component" :key="SDRouteCtxUpdateFix" />
          <!-- <component v-else :is="Component" /> -->
        </transition>
      </router-view>
    </LMain>
    </template>


    <Notifications />

    <Feedback />

    <!-- <AdsBlockerBait v-if="!DEV" /> -->

    <HotkeysHelper />

  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { LMain } from 'components/Layout';
import { notifyError, debounce, Format, noop, oneOf } from 'utils';
import { BROWSER } from 'const';
import router from 'router';
import ROUTES from 'routes';
import bokalSourage from 'services/bokalSourage';
import E from 'services/emitter';
import Feedback from 'components/Feedback';
import Sidebar from 'components/Sidebar';
import Topbar from 'components/Topbar';
import Notifications from 'components/Notifications';
import HotkeysHelper from 'components/HotkeysHelper';
// import AdsBlockerBait from 'components/AdsBlockerBait';
import Button from '@ohmsd/v-ui/components/Button';

import Identity from 'store/identity.entity';
import Apps from 'store/apps.resource';
import Advers from 'store/advertisers.resource';
import DataSources from 'store/dataSources.resource';
import AdsAccs from 'store/adsAccs.resource';

import AppNotFound from 'pages/Apps/AppNotFound';

import SDRouteCtx from './SDRouteCtx';

const Login = defineAsyncComponent(() => import(/*webpackChunkName: "Login"*/ 'pages/Login/Login'));

const toLogout = oneOf(ROUTES.CHANGE_PSWD, ROUTES.ACTIVATE, ROUTES.ACTIVATE_INVITED);
router.beforeEach((to, from, next) => {
  if (Identity.isAuth && toLogout(to.name))
    Identity.logout();
  next();
});

export default {
  name: 'Root',
  mixins: [SDRouteCtx({ onChange: 'onSDRouteCtxChange' })],

  data: () => ({
    topbarFix: 0,
    isAnyErrors: false,
    // app: null,
    updateFix: 1, // <router-view/> cant show Component if v-if vars isnt modified [SM-1028]

    sidebarMini: bokalSourage.get('Root.sidebarMini'),
  }),

  methods: {
    onSDRouteCtxChange (ctx) {
      if (ctx) {
        DataSources.fetch();
        if (ctx.adver)
          AdsAccs.fetch();
      } else {
        DataSources.reset();
        AdsAccs.reset();
      }
    },

    onSidebarToggle () {
      this.sidebarMini = !this.sidebarMini;
      setTimeout(() => E.emit('resize'), 100);
    },

    fetchApps (isAuth, prevIsAuth) {
      // console.log('Root.fetchApps', isAuth, prevIsAuth, this._lastIsAuth);
      if (isAuth && !prevIsAuth && !this._lastIsAuth) {
        Advers.fetch({ preserveData: true });
        Apps.fetch({ preserveData: true }).then(() => {
          // this.updateSDRouteCtx();
        });
      }
      else if (!isAuth) {
        Apps.reset();
        // DataSources.reset();
      }
      this._lastIsAuth = isAuth;
    },
  },

  watch: {
    sidebarMini (bool) { bokalSourage.set('Root.sidebarMini', !!bool) },

    isAuth: 'fetchApps',
  },

  computed: {
    isAuth () { return Identity.isAuth },

    showAny () { return Identity.ready },
    showLogin () { return !Identity.isAuth },
    showPanels () { return !this.showLogin },

    currency () {
      const { currency } = Identity.acc || {};
      return currency ? Format.currency(currency) : '';
    },
  },

  beforeCreate () {
    Identity.fetch({ preserveData: true })
      .then(() => {
        // SM-787 logout if user has come from email restoration
        if (this.$route.name === this.ROUTES.CHANGE_PSWD)
          Identity.logout();
        else
          this.fetchApps(this.isAuth);
      })
      .catch(noop);
  },

  created () {
    window.$App = this;

    // POS:FIX TOPBAR
    E.on('topbar:fix', bool => {
      (this.topbarFix += (bool ? 1 : -1));
    });

    E.on('sw:updatefound', () => {
      this.$notify({
        group: 'sw:updatefound',
        text: 'Updates!',
        duration: -1,
        data: { onConfirm: reload },
      });

      reload();

      function reload () { window.location.reload() }
    });

    this.fullbody = 0;
    E.on('fullbody', bool => {
      this.$el.classList.toggle('--fullbody', this.fullbody += (bool ? 1 : -1))
    });

    E.on('Identity:401', resp => {
      Identity.acc && Identity.logout();
    });

    this.notifyError = notifyError.bind(this);
    this.notify = this.$notify.bind(this);

    // RESIZING
    window.addEventListener('resize', debounce(e => {
      E.emit('resize', e);
      if (window.innerWidth <= 780)
        this.sidebarMini = true;
    }, 500), true);
  },

  mounted () {
    document.documentElement.classList.add(`--${ BROWSER.toLowerCase() }`);

    if (window.innerWidth <= 780)
      this.sidebarMini = true;
  },

  components: {
    LMain,
    Login,
    Sidebar,
    Topbar,
    Button,
    Feedback,
    AppNotFound,
    // AdsBlockerBait,
    HotkeysHelper,
    Notifications,
  },
}
</script>

<style>
#sd-mount {
  width: 100%;
  height: 100vh;
  /*overflow-y: overlay;
  overflow-x: hidden;*/
}

#sd-root {
  max-width: var(--page-mw);
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: row;
/*  flex-wrap: wrap;*/
/*  gap: 24px;*/
  .Topbar { flex-grow: 2 }

  &.--fullbody {
    .Topbar, .Sidebar, .crumbs, .Feedback { display: none }
    .l-main { margin: 0 }
  }

  .l-main {
    max-width: calc(100% - var(--side-w));
  }
}
</style>
